<script setup lang="ts">
    import { stripBlockTags } from '@/composables/helpers/html';
    const shardPng = '/img/2026/shard-registration.png';
    const shardMobilePng = '/img/2026/register-mobile-bottom-shard.png';
    const heroShardPng = '/img/2026/register-hero-bottom-shard.png';
    const heroTopShardPng = '/img/2026/register-hero-desktop-top-shard.png';
    const heroCenterBubblePng = '/img/2026/regsiter-hero-desktop-center-bubble.png';


    interface RegButton {
        label?: string;
        url?: string;
        newTab?: boolean;
    }

    const props = withDefaults(defineProps<{
        headline?: string;
        copy?: string;
        date?: string;
        location?: string;
        showDetails?: boolean;
        teal?: boolean;
        bizzabo?: boolean;
        partnerBizzabo?: boolean;
        useModal?: boolean;
        // UNB-673 (Bri Jackson): the "low availability" flag on the VIP button
        // was hardcoded, with no way to switch it off for a wave where the
        // sold-out tiers are not something to advertise. Defaults TRUE so every
        // existing Builder record keeps the behaviour it has now — the field is
        // absent on all of them, and a false default would silently remove it
        // everywhere.
        showLowAvailability?: boolean;
        // The same control for General Admission (Shannon, Asana 1217552685675868).
        // Defaults FALSE, which is the mirror of the reasoning above rather than a
        // contradiction of it: the VIP flag already existed, so switching it off by
        // default would have removed something live; the GA flag has never existed,
        // so switching it ON by default would add something nobody asked for to
        // every page the moment this deploys. Either way the default is "change
        // nothing until someone chooses to".
        showGaLowAvailability?: boolean;
        buttons?: RegButton[];
    }>(), {
        headline: 'Register for UNBOUND26',
        copy: '',
        date: 'September 16 - 18',
        location: 'Boston, MA',
        showDetails: true,
        bizzabo: false,
        partnerBizzabo: false,
        useModal: false,
        showLowAvailability: true,
        showGaLowAvailability: false,
    });

    const subHead = computed(() => {
        const parts = [props.date, props.location].filter(Boolean);
        return parts.join(' in ');
    });

    const hasCopy = computed(() => {
        if (!props.copy) return false;
        return props.copy.replace(/<[^>]*>/g, '').trim().length > 0;
    });

    const { data: regData } = useLazyAsyncData('registration', () => $fetch('/api/registration'));

    const defaultGa = {
        headline: 'General Admission',
        registerButtonLabel: 'Register for General Admission',
        details: [
            { data: { order: 1, description: '<p>Access to all in-person content and programming</p>' } },
            { data: { order: 2, description: '<p>Special hotel rates and a seamless booking experience with concierge support (while inventory lasts)</p>' } },
            { data: { order: 3, description: '<p>Access to educational content, entertainment, attendee networking, and connection opportunities with our sponsors</p>' } },
        ],
        tiers: [{ data: { status: 'active', price: 1199, label: 'Current' } }],
    };

    const defaultVip = {
        headline: 'VIP Admission',
        registerButtonLabel: 'Register for VIP Admission',
        description: 'Everything that comes with General Admission, plus:',
        details: [
            { data: { order: 1, description: '<p>Exclusive welcome party featuring live entertainment, food and beverages, and networking opportunities with fellow VIPs</p>' } },
            { data: { order: 2, description: '<p>VIP Lounge access featuring special amenities</p>' } },
            { data: { order: 3, description: '<p>Exclusive access to priority hotels with special hotel rates</p>' } },
            { data: { order: 4, description: '<p>Reserved seating at Spotlight sessions</p>' } },
            { data: { order: 5, description: '<p>Early access to session reservation for limited-capacity sessions</p>' } },
            { data: { order: 6, description: '<p>Dedicated VIP check-in</p>' } },
            { data: { order: 7, description: '<p>Complimentary lunch</p>' } },
        ],
        tiers: [{ data: { status: 'active', price: 1999, label: 'Current' } }],
    };

    const gaPass = computed(() => regData.value?.ga || defaultGa);
    const vipPass = computed(() => regData.value?.vip || defaultVip);

    // Find the active tier (or first tier) for pricing display
    const getActiveTier = (pass: any) => {
        if (!pass?.tiers?.length) return null;
        const sorted = [...pass.tiers].sort((a: any, b: any) => (a.data?.order || 0) - (b.data?.order || 0));
        return sorted.find((t: any) => t.data?.status === 'active')?.data || sorted[sorted.length - 1]?.data;
    };

    const gaTier = computed(() => getActiveTier(gaPass.value));
    const vipTier = computed(() => getActiveTier(vipPass.value));

    // A pass is fully sold out when none of its tiers are "active".
    // getActiveTier falls back to the last tier for price display, so we can't
    // rely on its truthiness to decide whether to show the register button.
    const passHasActiveTier = (pass: any) => !!pass?.tiers?.some((t: any) => t.data?.status === 'active');
    const gaHasActiveTier = computed(() => passHasActiveTier(gaPass.value));
    const vipHasActiveTier = computed(() => passHasActiveTier(vipPass.value));

    const additionalBlocks = computed(() => regData.value?.additonalBlocks || []);

    // Get the highest "future" tier price to show as crossed-out original price
    const getOriginalPrice = (pass: any) => {
        if (!pass?.tiers?.length) return null;
        const futureTiers = pass.tiers.filter((t: any) => t.data?.status === 'future');
        if (!futureTiers.length) return null;
        return Math.max(...futureTiers.map((t: any) => t.data?.price || 0));
    };

    const gaOriginalPrice = computed(() => getOriginalPrice(gaPass.value));
    const vipOriginalPrice = computed(() => getOriginalPrice(vipPass.value));

    const getSortedTiers = (pass: any) => {
        if (!pass?.tiers?.length) return [];
        return [...pass.tiers]
            .sort((a: any, b: any) => (a.data?.order || 0) - (b.data?.order || 0))
            .map(t => t.data);
    };

    const gaTiers = computed(() => getSortedTiers(gaPass.value));
    const vipTiers = computed(() => getSortedTiers(vipPass.value));

    const formatPassDate = (pass: any) => {
        if (!pass?.startDate || !pass?.endDate) return null;
        const start = new Date(pass.startDate);
        const end = new Date(pass.endDate);
        const month = start.toLocaleString('en-US', { month: 'long' });
        const startDay = start.getDate();
        const endDay = end.getDate();
        return `${month} ${startDay} - ${endDay}`;
    };

    const gaDate = computed(() => formatPassDate(gaPass.value) || props.date);
    const vipDate = computed(() => formatPassDate(vipPass.value) || props.date);

    const modalOpen = ref(false);

    const router = useRouter();
    const { $clientPosthog } = useNuxtApp();

    const goToRegistration = (e: Event) => {
        e.preventDefault();
        if (props.useModal || props.useModal === 'true') {
            modalOpen.value = true;
        } else {
            router.push('/register');
        }
    };

    // `pass` distinguishes the two purchasable products. Without it the only way
    // to count registration intent is matching autocapture button text, which
    // silently reported zero once the copy moved from "Tier 1" to "Tier 2".
    const handleRegisterClick = (e: Event, pass: 'general_admission' | 'vip' = 'general_admission') => {
        const source = pass === 'vip' ? vipPass.value : gaPass.value;
        const tier = pass === 'vip' ? vipTier.value : gaTier.value;
        $clientPosthog?.capture('cta_clicked', {
            cta_type: 'registration_pass',
            pass,
            button_label: source?.registerButtonLabel
                || (pass === 'vip' ? 'Register for VIP Admission' : 'Register for General Admission'),
            button_url: '/register',
            tier_label: tier?.label ?? null,
            price: tier?.price ?? null,
            opens_modal: !!(props.useModal || props.useModal === 'true'),
        });
        goToRegistration(e);
    };

    // Additional blocks (Partner Day, group rates) are not one of the two passes,
    // so they get their own cta_type rather than being miscounted as GA.
    const handleAdditionalBlockClick = (e: Event, block: any) => {
        $clientPosthog?.capture('cta_clicked', {
            cta_type: 'registration_additional_block',
            button_label: block?.buttonLabel || 'Register',
            button_url: block?.buttonUrl ?? null,
            block_headline: block?.headline ?? null,
            price: block?.price ?? null,
        });
        goToRegistration(e);
    };

    const sectionRef = ref(null);
    const leftColRef = ref(null);
    const leftTextRef = ref(null);
    const frozen = ref(false);
    const frozenTop = ref(0);
    const frozenWidth = ref(0);

    const handleScroll = () => {
        if (window.innerWidth < 1024) return;
        if (!sectionRef.value || !leftColRef.value || !leftTextRef.value) return;

        const sectionRect = sectionRef.value.getBoundingClientRect();
        const shouldFreeze = sectionRect.bottom <= window.innerHeight;

        if (shouldFreeze && !frozen.value) {
            // Capture current position relative to left column before switching
            const colRect = leftColRef.value.getBoundingClientRect();
            const textRect = leftTextRef.value.getBoundingClientRect();
            frozenTop.value = textRect.top - colRect.top;
            frozenWidth.value = textRect.width;
            frozen.value = true;
        } else if (!shouldFreeze && frozen.value) {
            frozen.value = false;
        }
    };

    const addPartnerDayScript = () => {
        const scriptId = 'bizzabo-sections-widget-script';
        if (!document.getElementById(scriptId)) {
            window.bz_widget_env = 'https://events.bizzabo.com';
            const e = document.createElement('script');
            e.id = scriptId;
            e.type = 'text/javascript';
            e.async = true;
            e.src = 'https://organizer.bizzabo.com/widgets/sections/sections.js';
            const t = document.getElementsByTagName('script')[0];
            t.parentNode.insertBefore(e, t);
        }
    };

    onMounted(() => {
        frozen.value = false;
        // Wait for scroll to settle after client-side navigation before enabling
        setTimeout(() => {
            window.addEventListener('scroll', handleScroll, { passive: true });
            window.addEventListener('resize', handleScroll, { passive: true });
            handleScroll();
        }, 300);

        if (props.partnerBizzabo) {
            addPartnerDayScript();
        }
    });

    onUnmounted(() => {
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleScroll);
    });
</script>

<template>
    <section ref="sectionRef"
        :class="['block-registration-2026 relative isolate overflow-clip', teal ? 'block-registration-hero' : 'bg-[var(--pink)]']">
        <!-- Hero background decorations -->
        <template v-if="teal">
            <img :src="heroTopShardPng" loading="lazy" decoding="async" alt="" aria-hidden="true"
                class="absolute top-0 right-0 pointer-events-none z-[0]" />
            <img :src="heroCenterBubblePng" loading="lazy" decoding="async" alt="" aria-hidden="true"
                class="hidden lg:display-block absolute top-0 left-0 w-[1010px] h-[1010px] pointer-events-none z-[0]" />
            <img :src="heroShardPng" loading="lazy" decoding="async" alt="" aria-hidden="true"
                class="absolute bottom-0 left-0 w-[774px] h-auto pointer-events-none z-[0]" />
        </template>
        <div :class="['flex flex-col gap-[42px] px-[var(--box-padding-lr-m)] py-[60px] lg:flex-row lg:items-start lg:justify-between lg:px-[40px] lg:pb-0',
            teal ? 'pt-[97px] lg:pt-[150px]' : 'lg:pt-[90px]']">
            <!-- Left: event details (sticky on desktop) -->
            <div ref="leftColRef" class="lg:flex-1 lg:min-w-0 lg:self-stretch lg:flex lg:flex-col lg:relative lg:z-[1]">
                <div
                    ref="leftTextRef"
                    :class="['flex flex-col gap-[24px] items-start relative z-[2]', frozen ? 'lg:absolute lg:left-0' : 'lg:sticky lg:top-[90px]']"
                    :style="frozen ? { top: frozenTop + 'px', width: frozenWidth + 'px' } : undefined">
                    <div :class="['flex flex-col gap-[12px]', teal ? 'text-white' : 'text-[var(--magenta)]']">
                        <h2 class="font-hubspot-serif normal-case mb-0 text-[56px] leading-[1.1] wrap-break-word lg:text-[calc(86/var(--base-fs)*1rem)]"
                            v-html="stripBlockTags(headline)" />
                        <p v-if="subHead" class="font-hubspot-sans font-light text-[20px] leading-[1.5] tracking-[0.4px] lg:text-[calc(24/var(--base-fs)*1rem)]">
                            {{ subHead }}
                        </p>
                        <div v-if="hasCopy" class="font-hubspot-sans font-light text-[16px] leading-[26px] tracking-[0.32px]" v-html="copy" />
                    </div>
                    <div v-if="buttons?.length" class="flex gap-[16px] items-center flex-wrap">
                        <MessLink v-for="(btn, i) in buttons" :key="i"
                            :href="btn.url || '#'"
                            :class="['inline-flex items-center gap-[10px] border rounded-[50px] px-[20px] py-[10px] h-[42px] font-hubspot-serif text-[16px] leading-[1.3] tracking-[0.32px] no-underline transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 lg:text-[calc(17/var(--base-fs)*1rem)] lg:leading-[1.4] lg:tracking-[0.34px] lg:h-auto',
                                teal
                                    ? 'border-white text-white hover:bg-[#D63D00] hover:border-[#D63D00] hover:text-white focus-visible:ring-white'
                                    : 'border-[var(--magenta)] text-[var(--magenta)] hover:bg-(--orange) hover:border-(--orange) hover:text-white focus-visible:ring-[var(--magenta)]']">
                            {{ btn.label }}
                        </MessLink>
                    </div>
                </div>
                <!-- Shard: sticks to viewport bottom on desktop (default mode only) -->
                <div v-if="!teal && !partnerBizzabo" class="hidden lg:display-block mt-auto pt-[240px] -ml-[40px] pointer-events-none z-[0] lg:sticky lg:bottom-0" aria-hidden="true">
                    <img :src="shardPng" loading="lazy" decoding="async" alt="" class="display-block w-[541px] max-w-none" />
                </div>
            </div>

            <!-- Right: ticket cards -->
            <div class="flex flex-col gap-[29px] lg:flex-1 lg:min-w-[555px] lg:pt-[15px] lg:pb-[90px] lg:gap-[38px] relative z-[3]">
                <!-- General Admission -->
                <div v-if="gaPass" class="bg-white rounded-(--radius) p-8 lg:p-11 flex flex-col gap-6 lg:gap-8">
                    <div class="flex flex-col gap-2">
                        <div class="flex items-center justify-between h-6 text-(--dark-pink)">
                            <span v-if="gaDate" class="font-hubspot-sans font-medium text-xs leading-normal tracking-[0.84px] uppercase">{{ gaDate }}</span>
                            <!-- Show crossed-out original (highest future tier) price if available -->
                            <span v-if="gaOriginalPrice"
                                class="font-hubspot-serif text-lg leading-[1.2] line-through lg:text-xl">
                                ${{ gaOriginalPrice.toLocaleString() }}
                            </span>
                        </div>
                        <div class="flex flex-col gap-5">
                            <div class="flex items-center justify-between font-hubspot-serif text-(--magenta) text-[25px] leading-[1.3] lg:text-[calc(30/var(--base-fs)*1rem)]">
                                <span>{{ gaPass.headline || 'General Admission' }}</span>
                                <span v-if="gaTier?.price && gaHasActiveTier" class="text-right">${{ gaTier.price.toLocaleString() }}</span>
                            </div>
                            <ul v-if="gaPass.details?.length" class="reg-feature-list flex flex-col gap-3.5 list-none p-0 m-0">
                                <li v-for="detail in gaPass.details.sort((a: any, b: any) => (a.data?.order || 0) - (b.data?.order || 0))"
                                    :key="detail.data?.order"
                                    class="reg-feature font-hubspot-sans font-light text-base leading-6.5 tracking-[0.32px] text-(--magenta)"
                                    v-html="detail.data?.description" />
                            </ul>
                        </div>
                    </div>
                    <!-- Tier pricing rows + register button -->
                    <div v-if="teal && gaTiers.length > 1" class="flex flex-col">
                        <!-- Sold out tiers -->
                        <div v-for="tier in gaTiers.filter(t => t.status === 'soldOut')" :key="tier.label"
                            class="flex items-center justify-between py-[13px] border-b border-[#F3E3EA]">
                            <span class="font-hubspot-serif text-lg leading-[1.2] text-(--dark-pink) line-through">{{ tier.label }}</span>
                            <span class="font-hubspot-sans font-medium text-xs leading-normal tracking-[0.84px] uppercase text-(--dark-pink)">sold out</span>
                        </div>
                        <!-- Register button at active tier -->
                        <a v-if="gaHasActiveTier" href="/register"
                            class="group flex flex-col items-center justify-center gap-1 w-[calc(100%+44px)] px-6 py-2.5 my-[15px] -mx-[22px] bg-(--magenta) rounded-[50px] font-hubspot-serif text-base leading-[1.3] tracking-[0.32px] text-white text-center no-underline hover:bg-(--orange) transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--magenta) focus-visible:ring-offset-2
                                lg:flex-row lg:justify-between lg:text-[calc(17/var(--base-fs)*1rem)] lg:leading-[1.4] lg:tracking-[0.34px]"
                            @click="handleRegisterClick($event, 'general_admission')">
                            <span class="flex flex-col items-center gap-1 lg:flex-row lg:items-center lg:gap-[18px]">
                                <span>{{ gaPass.registerButtonLabel || 'Register for General Admission' }}</span>
                                <span v-if="showGaLowAvailability && gaTier?.status === 'active' && gaPass.tiers?.filter((t: any) => t.data?.status === 'soldOut').length > 0"
                                    class="font-hubspot-sans font-medium text-[10px] leading-normal tracking-[0.7px] uppercase text-(--pink) group-hover:text-white transition-colors duration-200 lg:pt-[4px]">
                                    low availability
                                </span>
                            </span>
                            <span v-if="gaTier?.price">${{ gaTier.price.toLocaleString() }}</span>
                        </a>
                        <!-- Future tiers -->
                        <div v-for="tier in gaTiers.filter(t => t.status === 'future')" :key="tier.label"
                            class="flex items-center justify-between py-[13px] border-b border-[#F3E3EA]">
                            <span class="font-hubspot-serif text-lg leading-[1.2] text-[#A55C87]">{{ tier.label }}</span>
                            <span class="font-hubspot-serif text-lg leading-[1.2] text-[#A55C87]">${{ tier.price?.toLocaleString() }}</span>
                        </div>
                    </div>
                    <!-- Simple register button (non-hero or single tier) -->
                    <a v-else-if="gaHasActiveTier" href="/register"
                        class="group flex items-center justify-center gap-2.5 w-full px-6 py-2.5 bg-(--magenta) rounded-[50px] font-hubspot-serif text-base leading-[1.3] tracking-[0.32px] text-white text-center no-underline hover:bg-(--orange) transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--magenta) focus-visible:ring-offset-2 lg:text-[calc(17/var(--base-fs)*1rem)] lg:leading-[1.4] lg:tracking-[0.34px] lg:gap-5"
                        @click="handleRegisterClick($event, 'general_admission')">
                        <span class="flex flex-col items-center gap-1.5 lg:flex-row lg:items-center lg:gap-[18px]">
                            <span>{{ gaPass.registerButtonLabel || 'Register for General Admission' }}</span>
                            <span v-if="showGaLowAvailability && gaTier?.status === 'active' && gaPass.tiers?.filter((t: any) => t.data?.status === 'soldOut').length > 0"
                                class="font-hubspot-sans font-medium text-[10px] leading-normal tracking-[0.7px] uppercase text-(--pink) group-hover:text-white transition-colors duration-200 lg:pt-[4px]">
                                low availability
                            </span>
                        </span>
                    </a>
                </div>

                <!-- VIP Admission -->
                <div v-if="vipPass" class="bg-white rounded-(--radius) p-8 lg:p-11 flex flex-col gap-6 lg:gap-8">
                    <div class="flex flex-col gap-2">
                        <div class="flex items-center justify-between h-6 text-(--dark-pink)">
                            <span v-if="vipDate" class="font-hubspot-sans font-medium text-xs leading-normal tracking-[0.84px] uppercase">{{ vipDate }}</span>
                            <span v-if="vipOriginalPrice"
                                class="font-hubspot-serif text-lg leading-[1.2] line-through lg:text-xl">
                                ${{ vipOriginalPrice.toLocaleString() }}
                            </span>
                        </div>
                        <div class="flex flex-col gap-5">
                            <div class="flex items-center justify-between font-hubspot-serif text-(--magenta) text-[25px] leading-[1.3] lg:text-[calc(30/var(--base-fs)*1rem)]">
                                <span>{{ vipPass.headline || 'VIP Admission' }}</span>
                                <span v-if="vipTier?.price && vipHasActiveTier" class="text-right">${{ vipTier.price.toLocaleString() }}</span>
                            </div>
                            <div class="flex flex-col gap-4">
                                <div v-if="vipPass.description" class="font-hubspot-sans font-normal text-base leading-6.5 tracking-[0.192px] text-(--magenta)"
                                    v-html="vipPass.description" />
                                <ul v-if="vipPass.details?.length" class="reg-feature-list flex flex-col gap-3.5 list-none p-0 m-0">
                                    <li v-for="detail in vipPass.details.sort((a: any, b: any) => (a.data?.order || 0) - (b.data?.order || 0))"
                                        :key="detail.data?.order"
                                        class="reg-feature font-hubspot-sans font-light text-base leading-6.5 tracking-[0.32px] text-(--magenta)"
                                        v-html="detail.data?.description" />
                                </ul>
                            </div>
                        </div>
                    </div>
                    <!-- Tier pricing rows + register button -->
                    <div v-if="teal && vipTiers.length > 1" class="flex flex-col">
                        <!-- Sold out tiers -->
                        <div v-for="tier in vipTiers.filter(t => t.status === 'soldOut')" :key="tier.label"
                            class="flex items-center justify-between py-[13px] border-b border-[#F3E3EA]">
                            <span class="font-hubspot-serif text-lg leading-[1.2] text-(--dark-pink) line-through">{{ tier.label }}</span>
                            <span class="font-hubspot-sans font-medium text-xs leading-normal tracking-[0.84px] uppercase text-(--dark-pink)">sold out</span>
                        </div>
                        <!-- Register button at active tier -->
                        <a v-if="vipHasActiveTier" href="/register"
                            class="group flex flex-col items-center justify-center gap-1 w-[calc(100%+44px)] px-5 py-2.5 my-[15px] -mx-[22px] bg-(--magenta) rounded-[50px] font-hubspot-serif text-base leading-[1.3] tracking-[0.32px] text-white no-underline hover:bg-(--orange) transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--magenta) focus-visible:ring-offset-2
                                lg:flex-row lg:justify-between lg:text-[calc(17/var(--base-fs)*1rem)] lg:leading-[1.4] lg:tracking-[0.34px]"
                            @click="handleRegisterClick($event, 'vip')">
                            <span class="flex flex-col items-center gap-1 lg:flex-row lg:items-center lg:gap-[18px]">
                                <span>{{ vipPass.registerButtonLabel || 'Register for VIP Admission' }}</span>
                                <span v-if="showLowAvailability && vipTier?.status === 'active' && vipPass.tiers?.filter((t: any) => t.data?.status === 'soldOut').length > 0"
                                    class="font-hubspot-sans font-medium text-[10px] leading-normal tracking-[0.7px] uppercase text-(--pink) group-hover:text-white transition-colors duration-200 lg:pt-[4px]">
                                    low availability
                                </span>
                            </span>
                            <span v-if="vipTier?.price">${{ vipTier.price.toLocaleString() }}</span>
                        </a>
                        <!-- Future tiers -->
                        <div v-for="tier in vipTiers.filter(t => t.status === 'future')" :key="tier.label"
                            class="flex items-center justify-between py-[13px] border-b border-[#F3E3EA]">
                            <span class="font-hubspot-serif text-lg leading-[1.2] text-[#A55C87]">{{ tier.label }}</span>
                            <span class="font-hubspot-serif text-lg leading-[1.2] text-[#A55C87]">${{ tier.price?.toLocaleString() }}</span>
                        </div>
                    </div>
                    <!-- Simple register button (non-hero or single tier) -->
                    <a v-else-if="vipHasActiveTier" href="/register"
                        class="group flex flex-col items-center justify-center gap-1.5 w-full px-5 py-2.5 bg-(--magenta) rounded-[50px] font-hubspot-serif text-base leading-[1.3] tracking-[0.32px] text-white no-underline hover:bg-(--orange) transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--magenta) focus-visible:ring-offset-2 lg:flex-row lg:items-center lg:gap-5 lg:py-2.5 lg:text-[calc(17/var(--base-fs)*1rem)] lg:leading-[1.4] lg:tracking-[0.34px]"
                        @click="handleRegisterClick($event, 'vip')">
                        <span class="flex flex-col items-center gap-1.5 lg:flex-row lg:items-center lg:gap-[18px]">
                            <span>{{ vipPass.registerButtonLabel || 'Register for VIP Admission' }}</span>
                            <span v-if="showLowAvailability && vipTier?.status === 'active' && vipPass.tiers?.filter((t: any) => t.data?.status === 'soldOut').length > 0"
                                class="font-hubspot-sans font-medium text-[10px] leading-normal tracking-[0.7px] uppercase text-(--pink) group-hover:text-white transition-colors duration-200 lg:pt-[4px]">
                                low availability
                            </span>
                        </span>
                    </a>
                </div>

                <!-- Additional passes (e.g. Group General Admission) — hero mode only -->
                <template v-if="teal">
                <div v-for="(block, idx) in additionalBlocks" :key="idx"
                    class="bg-white rounded-(--radius) p-8 lg:p-11 flex flex-col gap-6 lg:gap-8">
                    <div class="flex flex-col gap-2">
                        <div class="flex items-center justify-between h-6 text-(--dark-pink)">
                            <span v-if="block.date" class="font-hubspot-sans font-medium text-xs leading-normal tracking-[0.84px] uppercase">{{ block.date }}</span>
                            <span v-if="block.originalPrice"
                                class="font-hubspot-serif text-lg leading-[1.2] line-through lg:text-xl">
                                ${{ block.originalPrice.toLocaleString() }}
                            </span>
                        </div>
                        <div class="flex items-center justify-between font-hubspot-serif text-(--magenta) text-[25px] leading-[1.3] lg:text-[calc(30/var(--base-fs)*1rem)]">
                            <span>{{ block.headline }}</span>
                            <span v-if="block.price" class="text-right">${{ block.price.toLocaleString() }}</span>
                        </div>
                        <div v-if="block.description" class="font-hubspot-sans font-light text-base leading-6.5 tracking-[0.32px] text-(--magenta)"
                            v-html="block.description" />
                    </div>
                    <a v-if="block.buttonUrl" :href="block.buttonUrl"
                        @click="handleAdditionalBlockClick($event, block)"
                        class="flex flex-col items-center justify-center gap-1 w-[calc(100%+44px)] px-5 py-2.5 -mx-[22px] bg-(--magenta) rounded-[50px] font-hubspot-serif text-base leading-[1.3] tracking-[0.32px] text-white text-center no-underline hover:bg-(--orange) transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--magenta) focus-visible:ring-offset-2
                            lg:flex-row lg:justify-between lg:text-[calc(17/var(--base-fs)*1rem)] lg:leading-[1.4] lg:tracking-[0.34px]">
                        <span>{{ block.buttonLabel || 'Register' }}</span>
                        <span v-if="block.price">${{ block.price.toLocaleString() }}</span>
                    </a>
                </div>
                </template>
            </div>
        </div>
        <!-- Partner Day Bizzabo widget -->
        <div v-if="partnerBizzabo" class="relative z-[1] w-full px-(--box-padding-lr)" id='partner-bizzabo-widget'>
            <div
                class="bizzabo-sections-widget rounded-(--radius)"
                style="display: inline-flex; width: 100%;"
                data-unique-name="756339"
                data-tab-id="5400053"
                data-internal-website-base-url="https://registration.inbound.com/26"
            ></div>
        </div>

        <!-- Mobile shard (default mode only) -->
        <img v-if="!teal && !partnerBizzabo" :src="shardMobilePng" loading="lazy" decoding="async" alt="" aria-hidden="true"
            class="lg:hidden absolute bottom-0 left-0 w-full h-auto pointer-events-none z-[0]" />

        <!-- Bizzabo registration modal -->
        <FragmentsDialogsRegistration v-if="useModal || useModal === 'true'" v-model:open="modalOpen" />
    </section>
</template>

<style>
    /* Feature list bullet dots */
    .reg-feature {
        display: flex;
        gap: 12px;
        align-items: flex-start;
    }

    .reg-feature::before {
        content: '';
        width: 5px;
        height: 6px;
        background: url('@/assets/img/2026/bullet-shard.svg') no-repeat center / contain;
        flex-shrink: 0;
        margin-top: 10px;
    }

    .block-registration-hero {
        background:
            radial-gradient(ellipse at 61% 22%, rgba(130, 12, 167, 1) 0%, rgba(100, 9, 105, 1) 49%, rgba(85, 8, 74, 1) 73%, rgba(70, 6, 43, 1) 98%);
    }

    #partner-bizzabo-widget {
        margin: 0 auto 80px auto;
    }

</style>
